/**
 * Australian Business Number (ABN) and Company Number (ACN) Validators
 * Based on Australian Government Taxation Office (ATO) and ASIC standards
 * Reference: ATO ABN Lookup, ASIC Company Register
 */

/**
 * Validates an Australian Business Number (ABN)
 * ABN Format: 11 digits (XX XXX XXX XXX)
 * Uses the modulo-89 checksum algorithm as per ATO standards
 * 
 * @param abn - The ABN string (with or without spaces/dashes)
 * @returns Object with isValid and message
 */
export function validateABN(abn: string): { isValid: boolean; message: string } {
  // Remove all non-digit characters
  const cleanAbn = abn.replace(/\D/g, '');

  // Check length is exactly 11 digits
  if (cleanAbn.length !== 11) {
    return {
      isValid: false,
      message: 'ABN must be exactly 11 digits',
    };
  }

  // Check if all characters are digits
  if (!/^\d{11}$/.test(cleanAbn)) {
    return {
      isValid: false,
      message: 'ABN must contain only numbers',
    };
  }

  /**
   * ABN Checksum Validation (ATO Standard Algorithm)
   * 1. Subtract 1 from the first digit
   * 2. Multiply each digit by its weighting factor
   * 3. Sum all the products
   * 4. Divide by 89, if remainder is 0, the ABN is valid
   */
  const abnDigits = cleanAbn.split('').map(Number);
  
  // Subtract 1 from the first digit
  abnDigits[0] = abnDigits[0] - 1;

  // Weighting factors for positions 1-11 (left to right)
  const weights = [10, 1, 3, 5, 7, 9, 11, 13, 15, 17, 19];

  // Calculate weighted sum
  let sum = 0;
  for (let i = 0; i < 11; i++) {
    sum += abnDigits[i] * weights[i];
  }

  // Check if sum is divisible by 89
  if (sum % 89 !== 0) {
    return {
      isValid: false,
      message: 'Invalid ABN - checksum validation failed',
    };
  }

  return {
    isValid: true,
    message: 'Valid ABN',
  };
}

/**
 * Validates an Australian Company Number (ACN)
 * ACN Format: 9 digits (XXX XXX XXX)
 * Uses the modulo-10 checksum algorithm as per ASIC standards
 * 
 * @param acn - The ACN string (with or without spaces/dashes)
 * @returns Object with isValid and message
 */
export function validateACN(acn: string): { isValid: boolean; message: string } {
  // Remove all non-digit characters
  const cleanAcn = acn.replace(/\D/g, '');

  // Check length is exactly 9 digits
  if (cleanAcn.length !== 9) {
    return {
      isValid: false,
      message: 'ACN must be exactly 9 digits',
    };
  }

  // Check if all characters are digits
  if (!/^\d{9}$/.test(cleanAcn)) {
    return {
      isValid: false,
      message: 'ACN must contain only numbers',
    };
  }

  /**
   * ACN Checksum Validation (ASIC Standard Algorithm)
   * 1. Multiply each of the first 8 digits by a weighting factor
   * 2. Sum the resulting products
   * 3. Divide by 10 and calculate remainder
   * 4. Subtract remainder from 10 (or use 0 if result is 10)
   * 5. This should equal the check digit (9th digit)
   */
  const acnDigits = cleanAcn.split('').map(Number);

  // Weighting factors for positions 1-8 (left to right)
  const weights = [8, 7, 6, 5, 4, 3, 2, 1];

  // Calculate weighted sum of first 8 digits
  let sum = 0;
  for (let i = 0; i < 8; i++) {
    sum += acnDigits[i] * weights[i];
  }

  // Calculate expected check digit
  const remainder = sum % 10;
  const expectedCheckDigit = remainder === 0 ? 0 : 10 - remainder;

  // Verify against actual check digit
  if (expectedCheckDigit !== acnDigits[8]) {
    return {
      isValid: false,
      message: 'Invalid ACN - checksum validation failed',
    };
  }

  return {
    isValid: true,
    message: 'Valid ACN',
  };
}

/**
 * Format ABN string to standard format (XX XXX XXX XXX)
 * @param abn - The ABN string (with or without existing formatting)
 * @returns Formatted ABN string
 */
export function formatABN(abn: string): string {
  const digits = abn.replace(/\D/g, '').slice(0, 11);
  
  if (digits.length === 0) return '';
  if (digits.length <= 2) return digits;
  if (digits.length <= 5) return `${digits.slice(0, 2)} ${digits.slice(2)}`;
  if (digits.length <= 8) return `${digits.slice(0, 2)} ${digits.slice(2, 5)} ${digits.slice(5)}`;
  return `${digits.slice(0, 2)} ${digits.slice(2, 5)} ${digits.slice(5, 8)} ${digits.slice(8)}`;
}

/**
 * Format ACN string to standard format (XXX XXX XXX)
 * @param acn - The ACN string (with or without existing formatting)
 * @returns Formatted ACN string
 */
export function formatACN(acn: string): string {
  const digits = acn.replace(/\D/g, '').slice(0, 9);
  
  if (digits.length === 0) return '';
  if (digits.length <= 3) return digits;
  if (digits.length <= 6) return `${digits.slice(0, 3)} ${digits.slice(3)}`;
  return `${digits.slice(0, 3)} ${digits.slice(3, 6)} ${digits.slice(6)}`;
}

/**
 * Quick check for ABN format (does not validate checksum)
 * Useful for real-time field validation feedback
 * @param abn - The ABN string
 * @returns boolean
 */
export function isValidABNFormat(abn: string): boolean {
  const cleanAbn = abn.replace(/\D/g, '');
  return /^\d{11}$/.test(cleanAbn);
}

/**
 * Quick check for ACN format (does not validate checksum)
 * Useful for real-time field validation feedback
 * @param acn - The ACN string
 * @returns boolean
 */
export function isValidACNFormat(acn: string): boolean {
  const cleanAcn = acn.replace(/\D/g, '');
  return /^\d{9}$/.test(cleanAcn);
}
